import { VatDeferral } from '@futrli-sdk/predict';
import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import { LOADER_AREAS } from '@futrli/ui-components';
import { trackPromise } from 'react-promise-tracker';
import { QueryParams } from '../types';
import { makeRequest, makeRequestWithResponse } from './shared';

/**
 * Fetch vat deferral
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @param {string} forecastId - forecastId for the forecast to get
 * @param {QueryParams} queryParams
 * @returns {Promise<>} - Returns forecast data
 * @throws error
 */
export const fetchVatDeferralById = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
): Promise<VatDeferral> => {
  const path = interpolatePathParams(API_PATHS.VAT_DEFERRAL, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<VatDeferral>({ params: queryParams, url: path });
};

/**
 * creates a vat deferral, using workspace, organaisation and forecast ID
 *
 * @param {VatDeferral} data
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecast to update
 * @param {QueryParams} queryParams
 * @returns {Promise} - Returns vat deferrals
 * @throws error
 */
export const createVatDeferral = async (
  data: VatDeferral,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
): Promise<VatDeferral> => {
  const path = interpolatePathParams(API_PATHS.VAT_DEFERRAL, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<VatDeferral>({ data, params: queryParams, url: path, method: REQUEST_METHODS.POST }),
    LOADER_AREAS.predictArea
  );
};

/**
 * updates a vat deferral, using workspace, organaisation and forecast ID
 *
 * @param {VatDeferral} data
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecast to update
 * @param {QueryParams} queryParams
 * @returns {Promise} - Returns vat deferrals
 * @throws error
 */
export const updateVatDeferral = async (
  data: VatDeferral,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
): Promise<VatDeferral> => {
  const path = interpolatePathParams(API_PATHS.VAT_DEFERRAL, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<VatDeferral>({ data, params: queryParams, url: path, method: REQUEST_METHODS.PUT }),
    LOADER_AREAS.predictArea
  );
};

/**
 * Deletes a vat deferral, using workspace, organaisation and forecast ID
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecast to update
 * @param {QueryParams} queryParams
 * @returns {Promise<void>} - Returns vat deferrals
 * @throws error
 */
export const deleteVatDeferral = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.VAT_DEFERRAL, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequest({ params: queryParams, url: path, method: REQUEST_METHODS.DELETE }),
    LOADER_AREAS.predictArea
  );
};
